import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

const { FiX, FiBookOpen, FiUser, FiTag, FiCalendar, FiLogIn, FiCheck, FiInfo, FiLoader } = FiIcons

const BookDetailModal = ({ book, isOpen, onClose, onCheckout, onReturn, isLoading, onLoginRedirect }) => {
  const { user, isGuest, isStaff } = useAuth()
  const [showSuccess, setShowSuccess] = useState(false)
  const [successType, setSuccessType] = useState('') // 'checkout' or 'return'

  if (!book) return null

  const handleAction = async () => {
    // 1. If Guest OR logged in but NOT staff/admin, they can't return books
    // We redirect to login/auth as requested if they try to interact with a checked-out book
    if (isGuest || (!isStaff && !book.isAvailable)) {
      onLoginRedirect()
      onClose()
      return
    }

    // 2. Checkout logic (Available to all logged-in users)
    if (book.isAvailable) {
      const res = await onCheckout(book.id)
      if (res?.success) {
        setSuccessType('checkout')
        triggerSuccess()
      }
    } 
    // 3. Return logic (Strictly Staff/Admin only)
    else if (book.currentLoan?.user_id === user?.id && isStaff) {
      const res = await onReturn(book.id, book.currentLoan.id)
      if (res?.success) {
        setSuccessType('return')
        triggerSuccess()
      }
    }
  }

  const triggerSuccess = () => {
    setShowSuccess(true)
    toast.success(successType === 'checkout' ? 'Enjoy your new read!' : 'Volume returned safely.')
    setTimeout(() => {
      setShowSuccess(false)
      onClose()
    }, 2000)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={onClose} 
            className="absolute inset-0 bg-space_indigo/90 backdrop-blur-md" 
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 40 }} 
            animate={{ opacity: 1, scale: 1, y: 0 }} 
            exit={{ opacity: 0, scale: 0.9, y: 40 }} 
            className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl relative z-10 overflow-hidden border-2 border-white-400 flex flex-col md:flex-row"
          >
            {/* Success Overlay */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  exit={{ opacity: 0 }} 
                  className="absolute inset-0 z-50 bg-dark_cyan flex flex-col items-center justify-center text-white p-8 text-center"
                >
                  <motion.div 
                    initial={{ scale: 0, rotate: -45 }} 
                    animate={{ scale: 1, rotate: 0 }} 
                    transition={{ type: "spring", damping: 12, stiffness: 200 }}
                    className="bg-white/20 p-6 rounded-full mb-6"
                  >
                    <SafeIcon icon={FiCheck} className="w-16 h-16" />
                  </motion.div>
                  <motion.h3 
                    initial={{ y: 20, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.2 }}
                    className="text-2xl font-black uppercase tracking-widest"
                  >
                    {successType === 'checkout' ? 'Checkout Confirmed!' : 'Return Complete!'}
                  </motion.h3>
                  <motion.p 
                    initial={{ y: 20, opacity: 0 }} 
                    animate={{ y: 0, opacity: 1 }} 
                    transition={{ delay: 0.3 }}
                    className="mt-2 text-white/80 font-bold uppercase tracking-widest text-[10px]"
                  >
                    {successType === 'checkout' ? 'This volume has been added to your shelf.' : 'Thank you for returning this volume.'}
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 z-20 p-2 bg-white/10 backdrop-blur-md rounded-full text-white md:text-space_indigo md:bg-white-500 hover:text-hot_berry transition-colors border border-white/20 md:border-white-400"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>

            <div className="w-full md:w-2/5 aspect-[3/4] md:aspect-auto bg-white-500 relative shrink-0">
              {book.cover_image ? (
                <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-white-500">
                  <SafeIcon icon={FiBookOpen} className="w-20 h-20 text-white-400" />
                </div>
              )}
              <div className="absolute bottom-6 left-6">
                <span className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 backdrop-blur-md shadow-xl ${book.isAvailable ? 'bg-dark_cyan text-white border-dark_cyan' : 'bg-hot_berry text-white border-hot_berry'}`}>
                  {book.isAvailable ? 'Available' : 'Currently Out'}
                </span>
              </div>
            </div>

            <div className="flex-1 p-8 md:p-12 overflow-y-auto custom-scrollbar flex flex-col">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="px-3 py-1 bg-white-500 border-2 border-white-400 rounded-lg text-[9px] font-black text-white-300 uppercase tracking-widest">
                    {book.category}
                  </div>
                  {book.published_year && (
                    <div className="px-3 py-1 bg-space_indigo-900 border-2 border-space_indigo-800 rounded-lg text-[9px] font-black text-space_indigo uppercase tracking-widest">
                      Year: {book.published_year}
                    </div>
                  )}
                </div>
                
                <h2 className="text-3xl md:text-4xl font-black text-space_indigo uppercase tracking-tight leading-none mb-2">
                  {book.title}
                </h2>
                <p className="text-lg font-black text-dark_cyan uppercase tracking-widest italic mb-8">
                  {book.author}
                </p>

                <div className="space-y-8">
                  <div>
                    <h4 className="text-[10px] font-black text-white-300 uppercase tracking-[0.2em] mb-3 flex items-center">
                      <SafeIcon icon={FiBookOpen} className="mr-2 w-3.5 h-3.5" /> Synopsis
                    </h4>
                    <p className="text-sm font-medium text-space_indigo/80 leading-relaxed">
                      {book.description || "No description provided for this volume."}
                    </p>
                  </div>

                  {book.tags && book.tags.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-black text-white-300 uppercase tracking-[0.2em] mb-3 flex items-center">
                        <SafeIcon icon={FiTag} className="mr-2 w-3.5 h-3.5" /> Classifications
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {book.tags.map((tag, i) => (
                          <span key={i} className="px-3 py-1.5 bg-white-500 text-space_indigo rounded-xl text-[9px] font-black uppercase tracking-wider border border-white-400">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-12 pt-8 border-t-2 border-white-400">
                <button 
                  onClick={handleAction} 
                  disabled={isLoading}
                  className={`w-full py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-xl flex items-center justify-center gap-3 ${
                    isGuest || (!isStaff && !book.isAvailable)
                      ? 'bg-space_indigo text-white hover:bg-space_indigo-400'
                      : book.isAvailable 
                      ? 'bg-dark_cyan text-white hover:bg-dark_cyan-400' 
                      : (book.currentLoan?.user_id === user?.id && isStaff)
                      ? 'bg-hot_berry text-white hover:bg-hot_berry-400'
                      : 'bg-white-500 text-white-300 border-2 border-white-400 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  ) : (isGuest || (!isStaff && !book.isAvailable)) ? (
                    <>
                      <SafeIcon icon={FiLogIn} className="w-4 h-4" />
                      Log In to Access
                    </>
                  ) : book.isAvailable ? (
                    <>
                      <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      Check Out Volume
                    </>
                  ) : (book.currentLoan?.user_id === user?.id && isStaff) ? (
                    <>
                      <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                      Return This Book
                    </>
                  ) : (
                    'Currently Unavailable'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default BookDetailModal